<template>
  <v-content>
    <v-card flat color="secondary" class="hero-card">
      <v-container class="d-flex flex-column flex-md-row">
        <div>
          <h1 class="display-2" :class="{
            'primary--text': !isMagenta
          }">
            Report Improperly Identified Calls
          </h1>
          <p class="mt-4">Help improve our Scam Likely service by letting us know if we got it wrong.</p>
        </div>

        <img
          src="@/assets/img/iphone-x.png"
          class="iPhone-img mx-auto mt-4"
          alt="iPhone with Scam Likely Calling"
        >
      </v-container>
    </v-card>

    <v-container class="py-12">
      <h2 class="display-1">
        Help us improve our service
      </h2>

      <p class="mt-6">
        Provide us with as much detail about the misidentified number
        that you would like to report to help us track down malicious
        actors and improve our Network smart services.
      </p>

      <p class="mt-6">
        <span class="font-weight-bold">Are you a business?</span> If
        you would like to submit a bulk list of numbers that you want
        to make sure are properly identified, please
        <a href="https://portal.firstorion.com/app/landing/#/signup">create a free account</a>
        and follow the steps to upload your numbers.
      </p>

      <p class="mt-6">
        If you are a business with registered numbers and feel that any
        of these numbers are being incorrectly tagged,
        <a href="https://support.firstorionenterprise.com/hc/en-us/requests/new">you may file a report.</a>
      </p>

      <hr class="mt-12 mb-12">

      <form class="mt-12" @submit.prevent="onSubmit">
        <h2 class="display-1 mt-12">
          What went wrong? <span class="primary--text title">* Required</span>
        </h2>

        <WhatWentWrong
          :category.sync="data.category"
          :reason.sync="data.reason"
        ></WhatWentWrong>

        <h2 class="display-1 mt-12">
          Call details
        </h2>

        <p class="mt-4">
          Any additional information you provide helps us better pin point
          culprits and improve our algorithms.
        </p>

        <v-row>
          <v-col lg="4">
            <v-text-field
              v-model="data.number"
              data-testid="input-phone-number"
              label="Phone Number"
              hint="* Required"
              persistent-hint
              required
              :outlined="!isMagenta"
            ></v-text-field>
          </v-col>

          <v-col lg="4">
            <v-text-field
              v-model="data.name"
              data-testid="input-caller-id"
              label="Incoming Caller ID"
              hint="Optional"
              persistent-hint
              :outlined="!isMagenta"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-col lg="4">
            <v-menu
              ref="dateMenu"
              v-model="dateMenu"
              :close-on-content-click="false"
              :return-value.sync="data.date"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="data.date"
                  data-testid="input-date-received"
                  label="Date Received"
                  hint="Optional"
                  persistent-hint
                  :prepend-icon="isMagenta ? 'mdi-calendar' : null"
                  :append-icon="!isMagenta ? 'mdi-calendar' : null"
                  readonly
                  v-on="on"
                  :outlined="!isMagenta"
                ></v-text-field>
              </template>
              <v-date-picker v-model="data.date" no-title scrollable>
                <v-btn text color="primary" @click="dateMenu = false">Cancel</v-btn>
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="$refs.dateMenu.save(data.date)">Select Date</v-btn>
              </v-date-picker>
            </v-menu>
          </v-col>

          <v-col lg="4">
            <v-menu
              ref="timeMenu"
              v-model="timeMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              :return-value.sync="data.time"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="formattedTime"
                  data-testid="input-call-time"
                  label="Call Time"
                  hint="Optional"
                  persistent-hint
                  :outlined="!isMagenta"
                  :prepend-icon="isMagenta ? 'mdi-clock' : null"
                  :append-icon="!isMagenta ? 'mdi-clock' : null"
                  readonly
                  v-on="on"
                >
                </v-text-field>
              </template>
              <v-time-picker
                v-if="timeMenu"
                v-model="data.time"
                ampm-in-title
                full-width
              >
                <template v-slot:default>
                  <v-btn text color="primary" @click="timeMenu = false">Cancel</v-btn>
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="$refs.timeMenu.save(data.time)">Select Time</v-btn>
                </template>
              </v-time-picker>
            </v-menu>
          </v-col>
        </v-row>

        <h2 class="display-1 mt-12">
          Additional information?
        </h2>

        <v-checkbox
          v-model="data.isPrerecorded"
          data-testid="checkbox-prerecorded"
          label="The call was an artificial or pre-recorded voice call."
        ></v-checkbox>

        <v-btn
          data-testid="btn-review"
          :color="isMagenta ? 'primary' : 'warning'"
          class="mt-12"
          type="submit"
          large
          width="150px"
          :style="{
            'border-radius': isMagenta ? 0 : undefined
          }"
          elevation="0"
        >Review</v-btn>
      </form>
    </v-container>
  </v-content>
</template>

<script lang="ts">
import { Vue, Component, PropSync } from 'vue-property-decorator'

import { convert24HTimeTo12H } from '@/utils'
import { CallDetailsInformation } from '@/types'
import WhatWentWrong from '@/components/WhatWentWrong.vue'

import config from '@/config'

@Component({
  components: {
    WhatWentWrong
  }
})
export default class CallDetails extends Vue {
  @PropSync('formStep')
  private step!: 'Details' | 'Review' | 'Confirmation'

  @PropSync('callDetails')
  private data!: CallDetailsInformation

  private dateMenu = false
  private timeMenu = false

  private onSubmit () {
    this.step = 'Review'
  }

  private get isMagenta () {
    return config.isMagenta
  }

  private get formattedTime () {
    return convert24HTimeTo12H(this.data.time)
  }
}
</script>

<style lang="scss" scoped>
.iPhone-img {
  height: 196px;
  width: auto;
  margin-left: auto;
  margin-bottom: -5rem;
  align-self: flex-end;
}
</style>
